import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#setupParentHoverListeners();
    this.#setupResourceImageHover();
    this.#setupLastHoverListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      setTimeout(() => {
        trapFocus(details);
      }, 0);
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);
      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    });
  }

  /**
   * Show child menus on parent hover (sticky until another parent is hovered).
   * Subgroup items (.menu-drawer__list-item--subgroup) stay visible at all times;
   * only the other child items follow the hover behavior.
   */
  #setupParentHoverListeners() {
    const parents = this.querySelectorAll('.menu-drawer__list-item--flat[data-parent]');
    let activeChildList = null;

    // Items gated by hover = direct children of the childList, excluding subgroups
    const getHoverItems = (childList) =>
      childList.querySelectorAll(':scope > .menu-drawer__list-item:not(.menu-drawer__list-item--subgroup)');

    /** @param {Element} childList @param {boolean} visible */
    const toggleItems = (childList, visible) => {
      getHoverItems(childList).forEach((item) => {
        item.style.opacity = visible ? '1' : '0';
        item.style.visibility = visible ? 'visible' : 'hidden';
      });
    };

    parents.forEach((parent) => {
      const parentIndex = parent.getAttribute('data-parent');
      const childList = this.querySelector(`.menu-drawer__menu.childList[data-parent="${parentIndex}"]`);

      if (childList) {
        // Container stays visible so subgroups are always shown
        childList.style.opacity = '1';
        childList.style.visibility = 'visible';
        childList.style.display = 'block';

        // Non-subgroup items are hidden by default and revealed on hover
        getHoverItems(childList).forEach((item) => {
          item.style.transition = 'opacity 0.3s ease, visibility 0.3s ease';
        });
        toggleItems(childList, false);
      }

      parent.addEventListener('mouseenter', () => {
        if (childList) {
          if (activeChildList && activeChildList !== childList) {
            toggleItems(activeChildList, false);
          }
          toggleItems(childList, true);
          activeChildList = childList;
        } else if (activeChildList) {
          toggleItems(activeChildList, false);
          activeChildList = null;
        }
      });
    });
  }


  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Setup hover effect for resource images (collections and pages)
   * Shows the appropriate image when hovering over menu items
   */
  #setupResourceImageHover() {
    const container = this.querySelector('.header-drawer-imgs');
    if (!container) return;

    const images = container.querySelectorAll('img[data-resource]');
    const links = this.querySelectorAll('.menu-drawer__menu-item');

    const reset = () => {
      container.classList.remove('active');
      images.forEach(img => img.classList.remove('active'));
    };

    links.forEach(link => {
      link.addEventListener('mouseenter', () => {
        const handle = link.dataset.resource;
        const resourceType = link.dataset.resourceType;

        // Lien sans resource → masquer tout
        if (!handle || !resourceType) {
          reset();
          return;
        }

        // Chercher l'image correspondant au handle ET au type de ressource
        const image = container.querySelector(
            `img[data-resource="${handle}"][data-resource-type="${resourceType}"]`
        );

        // Sécurité : ressource sans image
        if (!image) {
          reset();
          return;
        }

        // Activer l'image trouvée
        images.forEach(img => img.classList.remove('active'));
        container.classList.add('active');
        image.classList.add('active');
      });
    });

    // Quand on quitte toute la zone menu
    this.addEventListener('mouseleave', reset);
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }

  /**
 * Keep the last hovered item visually active until another is hovered
 */
  #setupLastHoverListeners() {
    const initGroup = (selector, parentSelector) => {
      this.querySelectorAll(parentSelector).forEach(parent => {
        const items = parent.querySelectorAll(selector);
        if (!items.length) return;

        items[0].classList.add('is-last-hovered');

        items.forEach(item => {
          item.addEventListener('mouseenter', () => {
            parent.querySelectorAll(selector).forEach(el => el.classList.remove('is-last-hovered'));
            item.classList.add('is-last-hovered');
          });
        });
      });
    };

    initGroup('.menu-drawer__menu-item--child', '.childList');
    initGroup('.menu-drawer__menu-item--mainlist', '.menu-drawer__menu');
  }

}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
